// Mint-Invoice › DRAWER "Lập hoá đơn": dữ liệu mẫu + kiểm trước phát hành theo code board. Đồng; chỉ màn có quyền `money`. Chỉ đọc.
import { INV_CUS, INV_LOCKED_PERIODS, ISS_ROWS } from './inv';

/** Thông tin xuất HĐ theo danh bạ khách: [tên, địa chỉ, email nhận HĐ]. */
export const DR_INFO: Readonly<Record<string, readonly [string, string, string]>> = {
  sa: ['CÔNG TY TNHH S••• VIỆT NAM', 'KCN Sóng Thần, TP. Dĩ An, Bình Dương', 'accounting@••••.vn'],
  an: ['CÔNG TY TNHH DỆT MAY AN PHÚ', 'Lô C3, KCN Tân Tạo, P. Tân Tạo A, Q. Bình Tân, TP.HCM', 'ketoan@anphutextile.vn'],
  tt: ['CÔNG TY TNHH NAMMY', '12 Đại lộ Bình Dương, TP. Thủ Dầu Một, Bình Dương', 'ketoan@nammy.vn'],
  sv: ['CÔNG TY TNHH DSF', '45 Nguyễn Văn Linh, Q.7, TP.HCM', 'ap@dsf.vn'],
  mh: ['CÔNG TY CP EMDEES FOODS', 'KCN Trà Nóc, Q. Bình Thuỷ, Cần Thơ', 'acc@emdees.vn'],
  hb: ['CÔNG TY TNHH RUITONG VIỆT NAM', 'KCN Nam Tân Uyên, Bình Dương', 'ketoan@ruitongvn.com'],
  dd: ['CÔNG TY CP MK AGRISEA', 'QL1A, TP. Sóc Trăng', 'ketoan@mkagrisea.vn'],
  pd: ['CÔNG TY TNHH TÂM NGỌC', '88 Lê Văn Việt, TP. Thủ Đức, TP.HCM', ''],
  da: ['CÔNG TY TNHH PHÚ CƯỜNG EXIM', '7 Trường Sơn, Q. Tân Bình, TP.HCM', 'ketoan@phucuong.vn'],
  bp: ['CÔNG TY TNHH VĨNH PHÁT', 'KCN Đồng Xoài, Bình Phước', 'ketoan@vinhphat.vn'],
  ml: ['CÔNG TY CP QUẾ KỲ', '', 'ketoan@queky.vn'],
};

export const DR_FX_JOB = 25400;
export const DR_CHI_HO = 1250000;
/** Dòng phí: [mã, diễn giải, SL, đơn giá, VAT, thành tiền theo tỷ giá → số]. OCF theo USD × tỷ giá. */
export const drLines = (fx: number) => ([
  ['OCF', 'O/F 2×40HC VNSGN → KRPUS', '2', '3.000 USD', '0%', 6000 * fx],
  ['THC', 'THC đầu xuất', '2', '4.200.000', '8%', 8400000],
  ['BLF', 'Phí phát hành B/L', '1', '1.200.000', '8%', 1200000],
  ['CCF', 'Khai hải quan xuất', '1', '1.500.000', '8%', 1500000],
] as const);
export const drTotals = (fx: number) => {
  const net = 6000 * fx + 8400000 + 1200000 + 1500000;
  const vat = (8400000 + 1200000 + 1500000) * 0.08;
  return { net, vat, total: net + vat + DR_CHI_HO };
};

const sp = (x: string): string => x.replace(/\s/g, '');
export const drMstDiff = (cus: string): boolean => cus === 'hb';
export const drDefaultMst = (cus: string): string => (drMstDiff(cus) ? '3702 667 091' : INV_CUS[cus]![1]);
export const emailOk = (e: string): boolean => /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(e.trim());

export interface DrIn { cus: string; mst: string; looked: boolean; addr: string | undefined; email: string | undefined; periodLocked: boolean }
export interface DrCheck { k: string; ok: boolean; v: string }
/** Kiểm trước phát hành (6 mục) — nguồn: board. */
export function drChecks(i: DrIn): { name: string; addr: string; email: string; mstOk: boolean; checks: DrCheck[] } {
  const info = DR_INFO[i.cus]!;
  const mstOk = i.looked || (sp(i.mst) === sp(INV_CUS[i.cus]![1]) && !drMstDiff(i.cus));
  const name = mstOk ? info[0] : '';
  const addr = i.addr ?? (mstOk ? info[1] : '');
  const email = i.email ?? info[2];
  const ok = emailOk(email);
  return {
    name, addr, email, mstOk,
    checks: [
      { k: 'Mã số thuế đã tra, khớp danh bạ', ok: mstOk, v: mstOk ? INV_CUS[i.cus]![1] : drMstDiff(i.cus) ? 'MST khác danh bạ — bấm Tra MST' : 'Chưa tra MST' },
      { k: 'Tên đơn vị người mua', ok: !!name, v: name ? 'Có' : 'Trống — tra MST' },
      { k: 'Địa chỉ xuất hoá đơn', ok: !!addr.trim(), v: addr.trim() ? 'Có' : 'Thiếu địa chỉ' },
      { k: 'Có dòng phí', ok: true, v: '4 dòng + 1 chi hộ' },
      { k: 'Email nhận hoá đơn', ok, v: ok ? email.trim() : email.trim() ? 'Email sai định dạng' : 'Thiếu email nhận HĐ' },
      { k: 'Kỳ kế toán 10/2026 đang mở', ok: !i.periodLocked, v: i.periodLocked ? 'Kỳ đã khoá' : 'Đang mở' },
    ],
  };
}
export const drPeriodLocked = (): boolean => INV_LOCKED_PERIODS.includes(10);
/** Debit mở sẵn trong drawer = dòng đầu của Chờ xuất HĐ. */
export const DR_ROW = ISS_ROWS[0]!;
